<template>
  <ui-dialog
    :show="isOpen"
    persistent
    scrollable
    max-width="623"
    custom-class="external-changes-group-change-dialog"
    @input="close"
  >
    <template #header>
      <header v-if="isAccept || isReject" class="header">
        <h3>
          {{
            $t(
              `message.externalChanges.groupChangeDialog.${chosenAction}.title`
            )
          }}
        </h3>
        <p>
          {{
            $t(`message.externalChanges.groupChangeDialog.${chosenAction}.text`)
          }}
        </p>
        <InfoBox
          v-if="chosenSource"
          class="mt-2"
          :text="
            $t(
              `message.externalChanges.groupChangeDialog.${chosenAction}.${chosenSource}`
            )
          "
        />
        <ItemsCounter
          class="my-2"
          v-if="selectedFacilitiesCount"
          prefixText="message.externalChanges.selectedFacilities"
          :count="selectedFacilitiesCount"
        />
      </header>
    </template>
    <template #default>
      <div class="group-change">
        <v-list v-if="fieldsList.length">
          <v-list-item
            ripple
            class="list-item list-item--all"
            @click="toggleAllFields"
          >
            <v-checkbox :inputValue="isAllFieldsSelected"></v-checkbox>
            <v-list-item-content>
              <v-list-item-title>
                <p class="list-item__name">
                  {{ $t("message.common.selectAll") }}
                </p>
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <ItemsCounter
                v-if="chosenFields.length"
                :count="chosenFields.length"
              />
            </v-list-item-action>
          </v-list-item>
          <v-virtual-scroll
            :items="fieldsList"
            :item-height="listItemHeight"
            :height="listHeight"
          >
            <template v-slot:default="{ item }">
              <v-list-item
                :key="item.id"
                ripple
                @click="toggleField(item.value)"
                class="list-item"
              >
                <v-checkbox :inputValue="item.selected"></v-checkbox>
                <v-list-item-content>
                  <p class="list-item__name">{{ item.label }}</p>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-virtual-scroll>
        </v-list>
        <no-data v-else></no-data>
      </div>
    </template>
    <template #actions>
      <ui-button outlined @click="close">
        {{ $t("message.common.close") }}
      </ui-button>
      <ui-button
        v-if="chosenAction"
        color="primary"
        @click="save"
        :disabled="!chosenFields.length"
      >
        {{
          $t(`message.externalChanges.groupChangeDialog.${chosenAction}.button`)
        }}
      </ui-button>
    </template>
  </ui-dialog>
</template>

<script>
import {
  EXTERNAL_CHANGES_STATUS,
  EXTERNAL_CHANGES_SOURCE,
  EXTERNAL_CHANGES_FIELD
} from "@/enums/external-changes";

import InfoBox from "@/components/ui/InfoBox.vue";
import ItemsCounter from "@/components/List/ItemsCounter";
import NoData from "@/components/ui/NoData";

export default {
  name: "GroupChangeDialog",
  data() {
    return {
      fieldsList: [],
      listItemHeight: 55
    };
  },

  components: {
    InfoBox,
    ItemsCounter,
    NoData
  },

  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    chosenAction: {
      type: Number,
      validator: value => Object.values(EXTERNAL_CHANGES_STATUS).includes(value)
    },
    chosenSource: {
      type: Number,
      validator: value =>
        Object.values(EXTERNAL_CHANGES_SOURCE).includes(value) || value === null
    },
    selectedFacilitiesCount: {
      type: Number
    }
  },

  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.setFields();
      }
    }
  },

  computed: {
    isAccept() {
      return this.chosenAction === EXTERNAL_CHANGES_STATUS.ACCEPTED;
    },
    isReject() {
      return this.chosenAction === EXTERNAL_CHANGES_STATUS.REJECTED;
    },
    isAllFieldsSelected() {
      return (
        this.fieldsList.length && this.fieldsList.every(field => field.selected)
      );
    },
    isNoFieldsSelected() {
      return (
        this.fieldsList.length && !this.fieldsList.some(field => field.selected)
      );
    },
    chosenFields() {
      return this.fieldsList.filter(field => field.selected);
    },
    listHeight() {
      return this.listItemHeight * 8;
    }
  },

  methods: {
    setFields() {
      let values = [...Object.values(EXTERNAL_CHANGES_FIELD)];

      if (this.isReject) {
        values = values.filter(
          val => val !== EXTERNAL_CHANGES_FIELD.LOCATION_STATE
        );
      }

      this.fieldsList = values.map(value => {
        const field = {
          value,
          label: this.$t(`message.externalChanges.fields.${value}`),
          selected: false
        };
        return field;
      });
    },

    toggleField(fieldValue) {
      const foundIndex = this.fieldsList.findIndex(
        ({ value }) => value === fieldValue
      );
      this.fieldsList[foundIndex].selected = !this.fieldsList[foundIndex]
        .selected;
    },

    toggleAllFields() {
      if (this.isAllFieldsSelected) {
        this.fieldsList.forEach(field => {
          field.selected = false;
        });
      } else if (this.isNoFieldsSelected) {
        this.fieldsList.forEach(field => {
          field.selected = true;
        });
      } else {
        this.fieldsList.forEach(field => {
          if (!field.selected) {
            field.selected = true;
          }
        });
      }
    },

    isFieldSelected(fieldValue) {
      const foundIndex = this.fieldsList.findIndex(
        ({ value }) => value === fieldValue
      );
      return this.fieldsList[foundIndex].selected;
    },

    close() {
      this.$emit("close");
    },

    save() {
      this.$emit("save", {
        fields: this.chosenFields.map(field => field.value)
      });
      this.close();
    }
  }
};
</script>

<style lang="scss">
.external-changes-group-change-dialog {
  .v-card > .v-card__title {
    padding-bottom: 0 !important;
  }
}
</style>

<style lang="scss" scoped>
.group-change {
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  .list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-right: 0 !important;
    padding-left: $spacing * 1.5;
    width: 100%;
    height: 55px;

    &:not(:last-child) {
      border-bottom: 1px solid $light-3;
    }

    &__name {
      @include font-size(14px);
      margin-bottom: 0;
      line-height: 24px;
    }

    .v-list-item__content {
      padding-left: $spacing;
    }
  }

  .v-radio--is-disabled > * {
    opacity: 0.5;
  }
}

header {
  padding-left: $spacing * 1.5;
  border-bottom: 1px solid $light-3;

  h3 {
    @include font-size(22px);
    margin-bottom: $spacing * 3;
  }

  p {
    @include font-size(16px);
    line-height: 19.5px;
    margin-bottom: 0 !important;
    opacity: 0.9;
  }
}

.v-label {
  padding-right: $spacing;
}

.dialog-header {
  margin-right: 0 !important;
}

.v-input--selection-controls__input {
  margin-right: $spacing * 1.5;
}

.v-card__title {
  padding-bottom: $spacing * 3;

  h3 {
    line-height: 36px;
  }
}
</style>
